/**
 * Utility functions for appointment-related operations
 * These functions use the backend enum values fetched from the API
 */
import { appointmentTypesService } from '../services/appointment-types/appointmentTypesService';

// Cache for synchronous access (populated on first use when a page needs appointment type labels)
let appointmentTypesCache: Map<string, { display_name: string; is_online_consultation: boolean }> = new Map();
let loadStarted = false;

/** Fallback map used until API types are loaded (matches backend enum) */
const FALLBACK_MAP: Map<string, { display_name: string; is_online_consultation: boolean }> = new Map([
  ['online_consultation', { display_name: 'Online Consultation', is_online_consultation: true }],
  ['hospital_visit', { display_name: 'Hospital Visit', is_online_consultation: false }],
  ['consultation', { display_name: 'Online Consultation', is_online_consultation: true }],
  ['follow_up', { display_name: 'Follow-up', is_online_consultation: false }],
  ['emergency', { display_name: 'Emergency', is_online_consultation: false }],
]);

/**
 * Load appointment types from API when a page that uses them is first rendered.
 * Called lazily on first use of getConsultationTypeLabel / isOnlineConsultation (no preload).
 */
function loadAppointmentTypesIfNeeded(): void {
  if (loadStarted) return;
  loadStarted = true;
  appointmentTypesService.getAppointmentTypes().then((types) => {
    appointmentTypesCache.clear();
    types.forEach((type) => {
      appointmentTypesCache.set(type.value, {
        display_name: type.display_name,
        is_online_consultation: type.is_online_consultation,
      });
    });
  });
}

function getCache(): Map<string, { display_name: string; is_online_consultation: boolean }> {
  if (appointmentTypesCache.size === 0) {
    loadAppointmentTypesIfNeeded();
    return FALLBACK_MAP;
  }
  return appointmentTypesCache;
}

/**
 * Get the consultation type label for display
 * Uses cached values from backend API, with fallback for immediate access
 * @param appointmentType - The appointment type from the backend (enum value)
 * @returns The display label for the consultation type with emoji
 */
export const getConsultationTypeLabel = (appointmentType?: string): string => {
  if (!appointmentType) return '🏥 Hospital Visit';
  
  const cache = getCache();
  const cached = cache.get(appointmentType);
  const displayName = cached?.display_name || 'Hospital Visit';
  const isOnline = cached?.is_online_consultation ?? false;
  
  // Add emoji based on type
  if (isOnline) {
    return `📹 ${displayName}`;
  }
  
  return `🏥 ${displayName}`;
};

/**
 * Check if an appointment is an online consultation
 * Uses cached values from backend API
 * @param appointmentType - The appointment type from the backend (enum value)
 * @returns True if the appointment is an online consultation
 */
export const isOnlineConsultation = (appointmentType?: string): boolean => {
  if (!appointmentType) return false;
  const cached = getCache().get(appointmentType);
  return cached?.is_online_consultation ?? false;
};
